import { Controller } from "@hotwired/stimulus";

/**
 * Live-mirrors the survey title onto the builder header `<h1>` as the user
 * edits it in the Settings drawer. Listens on `window` for the
 * `survey:title-changed` CustomEvent dispatched by
 * `survey_title_field_controller.ts`.
 *
 * Mounts on the `<h1>` itself. An empty `detail.title` is ignored on purpose:
 * the field is `required`, so an empty value would never persist — blanking
 * the header would show a state the server will never accept and would also
 * cause a small layout shift. The header resumes tracking the input at the
 * first keystroke of the replacement.
 *
 * Uses `textContent` (not `innerHTML`) so user input is rendered safely by
 * construction, matching the initial Rails-escaped `<%= survey.title %>`.
 */
export default class extends Controller<HTMLElement> {
  private boundOnChange = (event: Event) => this.onChange(event);

  connect() {
    window.addEventListener("survey:title-changed", this.boundOnChange);
  }

  disconnect() {
    window.removeEventListener("survey:title-changed", this.boundOnChange);
  }

  private onChange(event: Event) {
    const title = (event as CustomEvent).detail?.title;
    if (typeof title === "string" && title.length > 0) {
      this.element.textContent = title;
    }
  }
}
